import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import U2000BasicTableList from '../../../u2000Core/components/tables/basic/U2000BasicTableList';
import type { U2000TableColumn } from '../../../u2000Core/components/tables/basic/U2000BasicTableList';
import U2000CommonTableFooter from '../../../u2000Core/components/tables/basic/U2000CommonTableFooter';
import { useU2000GridStateHandlers } from '../../../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import { u2000ComputeHeaderSortFields } from '../../../u2000Core/tools/u2000ApplyHeaderSort';
import type { SortedFieldItem } from '../../../u2000Core/components/tables/commons/search/U2000BasicSearchSort';
import { NS } from '../../U3146';
import { u3146SortPreviewGridRows } from '../../u3146SortPreviewGridRows';
import { u3146PreviewLineIsEligible, type U3146PreviewLine } from '../../U3146Service';
import {
  u3146InitCloseInactivePreviewGridColumns,
  u3146PreviewLineToGridRow,
  type U3146PreviewGridRow,
} from './u3146InitCloseInactivePreviewGrid';

export type U3146CloseInactiveWizardPreviewGridProps = {
  lines: U3146PreviewLine[];
  selectedProjectIds: Set<string>;
  onToggleProject: (projectId: string, checked: boolean) => void;
  /** Si `visibleEligibleProjectIds` est fourni (filtre actif), ne sélectionne que ces IDs. */
  onToggleAllEligible: (checked: boolean, visibleEligibleProjectIds?: string[]) => void;
  excludeIneligible?: boolean;
  readonly?: boolean;
};

const U3146CloseInactiveWizardPreviewGrid: React.FC<U3146CloseInactiveWizardPreviewGridProps> = ({
  lines,
  selectedProjectIds,
  onToggleProject,
  onToggleAllEligible,
  excludeIneligible = false,
  readonly = false,
}) => {
  const { t, i18n } = useTranslation();
  const gridStateHandlers = useU2000GridStateHandlers<U3146PreviewGridRow>();

  const hasEligibleRows = useMemo(() => lines.some(u3146PreviewLineIsEligible), [lines]);

  const filteredLines = useMemo(
    () => lines.filter((line) => !excludeIneligible || u3146PreviewLineIsEligible(line)),
    [lines, excludeIneligible],
  );

  const selectionKey = useMemo(() => {
    const ids = [...selectedProjectIds].sort();
    return ids.join(',');
  }, [selectedProjectIds]);

  /** Inclut le détail TEC pour re-synchroniser la grille après enrichissement API (ex. recent_billable_tec). */
  const linesKey = useMemo(
    () =>
      lines
        .map((line) => {
          if (line.status === 'eligible') {
            return `${line.projectId}:eligible:${line.lastBillableTecDate}:${line.daysSinceLastBillableTec}:${line.uninvoicedTecLineCount}:${line.uninvoicedTecAmountTec}`;
          }
          if (line.status === 'not_eligible' && line.reason === 'recent_billable_tec') {
            return `${line.projectId}:recent_tec:${line.lastBillableTecDate}:${line.daysSinceLastBillableTec}:${line.uninvoicedTecLineCount}:${line.uninvoicedTecAmountTec}`;
          }
          return `${line.projectId}:${line.status}:${line.reason}`;
        })
        .join('|'),
    [lines],
  );

  const columns = useMemo(
    () => u3146InitCloseInactivePreviewGridColumns(t, { readonly, hasEligibleRows }),
    [t, readonly, hasEligibleRows],
  );

  const defaultSortedFields = useMemo(
    () =>
      columns
        .filter((col) => col.isDefaultSortedColumn)
        .map((col) => ({
          name: col.name,
          sortOrder: (col.defaultSortOrder === 'DESC' ? 'DESC' : 'ASC') as 'ASC' | 'DESC',
        })),
    [columns],
  );

  useEffect(() => {
    gridStateHandlers.setColumns(columns);
    gridStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
    gridStateHandlers.setSelectedSortedFields(defaultSortedFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- gridStateHandlers volontairement exclu
  }, [columns, defaultSortedFields, i18n.language]);

  const syncGridData = useCallback(
    (sortedFieldsOverride?: { name: string; sortOrder?: string }[]) => {
      const fromState = gridStateHandlers.getSelectedSortedFields();
      const sortedFields =
        sortedFieldsOverride?.length
          ? sortedFieldsOverride
          : fromState.length > 0
            ? fromState
            : defaultSortedFields;
      const rows = filteredLines.map((line) =>
        u3146PreviewLineToGridRow(line, selectedProjectIds.has(line.projectId)),
      );
      const sorted = u3146SortPreviewGridRows(rows, sortedFields, columns);
      gridStateHandlers.setData(sorted);
      gridStateHandlers.setTotalRows(sorted.length);
      gridStateHandlers.setErrors([]);
    },
    [columns, defaultSortedFields, filteredLines, gridStateHandlers, selectedProjectIds],
  );

  useEffect(() => {
    syncGridData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync sur clés métier + filtre
  }, [linesKey, selectionKey, excludeIneligible, columns, defaultSortedFields]);

  const onColumnHeaderSortClick = useCallback(
    (column: U2000TableColumn) => {
      const currentSorted: SortedFieldItem[] = gridStateHandlers
        .getSelectedSortedFields()
        .map((field) => ({
          field: columns.find((col) => col.name === field.name) ?? { name: field.name, label: field.name, type: 'string' },
          sortOrder: (field.sortOrder === 'DESC' ? 'DESC' : 'ASC') as 'ASC' | 'DESC',
        }));
      const next = u2000ComputeHeaderSortFields(column, currentSorted);
      if (!next) {
        return;
      }
      const newSortedFields = next.map((item) => ({
        name: item.field.name,
        sortOrder: item.sortOrder,
      }));
      gridStateHandlers.setSelectedSortedFields(newSortedFields);
      syncGridData(newSortedFields);
    },
    [columns, gridStateHandlers, syncGridData],
  );

  const isRowSelectable = useCallback((row: U3146PreviewGridRow) => row.eligible, []);

  const rowUpdated = useCallback(
    (row: U3146PreviewGridRow & { idChecked?: boolean }) => {
      if (!row.eligible || readonly) return;
      onToggleProject(row.id, row.idChecked === true);
    },
    [onToggleProject, readonly],
  );

  const handleSelectAllRow = useCallback(
    (isSelected: boolean) => {
      if (readonly) return;
      const data = gridStateHandlers.getData();
      const updated = data.map((row) => ({
        ...row,
        idChecked: row.eligible ? isSelected : false,
      }));
      gridStateHandlers.setData(updated);
      if (isSelected) {
        const visibleEligibleIds = data.filter((row) => row.eligible).map((row) => row.projectId);
        onToggleAllEligible(true, excludeIneligible ? visibleEligibleIds : undefined);
      } else {
        onToggleAllEligible(false);
      }
    },
    [gridStateHandlers, onToggleAllEligible, readonly, excludeIneligible],
  );

  if (lines.length === 0) {
    return <p className="text-sm text-gray-500">{t('wizardPreviewEmpty', { ns: NS })}</p>;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xs">
      <div
        className={[
          'min-h-0 flex-1 overflow-y-auto overflow-x-hidden',
          '[&_div.rounded-lg]:mt-0 [&_div.rounded-lg]:rounded-none [&_div.rounded-lg]:border-0 [&_div.rounded-lg]:shadow-none',
          '[&_div.rounded-lg]:overflow-x-hidden [&_div.rounded-lg>div]:overflow-x-hidden',
          '[&_table]:w-full [&_table]:table-fixed',
          '[&_table]:border-separate [&_table]:border-spacing-0',
          '[&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-20 [&_thead_th]:bg-gray-100',
          '[&_thead_th]:px-2 [&_thead_th]:py-2 [&_thead_th]:text-left [&_thead_th]:align-middle',
          '[&_thead_th]:text-xs [&_thead_th]:font-medium [&_thead_th]:leading-tight',
          '[&_thead_th]:shadow-[0_1px_0_0_rgb(229_231_235)]',
          '[&_tbody_tr:last-child]:border-b [&_tbody_tr:last-child]:border-gray-200',
        ].join(' ')}
      >
        <U2000BasicTableList
          gridStateHandlers={gridStateHandlers}
          allowRowSelection={!readonly}
          isRowSelectable={isRowSelectable}
          rowUpdated={rowUpdated}
          handleSelectAllRow={handleSelectAllRow}
          onColumnHeaderSortClick={onColumnHeaderSortClick}
        />
      </div>
      <footer className="form-card-footer shrink-0 py-3">
        <U2000CommonTableFooter
          nbItem={gridStateHandlers.getTotalRows()}
          label={t('lblProject', { count: gridStateHandlers.getTotalRows(), ns: NS })}
        />
      </footer>
    </div>
  );
};

export default U3146CloseInactiveWizardPreviewGrid;
